import React, { useEffect, useState } from "react";
import { Sparkles, LineChart, MessageSquareText, Mail, Send, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Label } from "../../components/common";
import { Button } from "../../components/ui/button";
import { ExecutiveBrief, CommentInsights } from "../../components/ai";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import AiChat from "./AiChat";

function DigestCard() {
  const [configured, setConfigured] = useState(null);
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/ai/digest/preview").then((r) => { setConfigured(r.data.email_configured); setTo(r.data.to); }).catch(() => {});
  }, []);

  const sendTest = async () => {
    setSending(true);
    try { await api.post("/ai/digest/test"); toast.success(`Test digest sent to ${to}`); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-indigo-100 bg-indigo-50/40 p-4" data-testid="digest-card">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-[#4338CA] shadow-sm"><Mail className="h-4.5 w-4.5" /></span>
        <div>
          <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><CalendarClock className="h-3.5 w-3.5 text-[#4338CA]" /> Weekly email digest</p>
          <p className="text-xs text-slate-500">
            Your AI brief is emailed every <b>Monday 09:30</b> (SAST).{" "}
            {configured === false && <span className="text-amber-600">Email delivery isn't configured yet. Add your Amazon SES keys to activate.</span>}
          </p>
        </div>
      </div>
      <Button onClick={sendTest} disabled={sending || configured === false} data-testid="send-test-digest"
        variant="outline" className="border-indigo-200 text-[#4338CA] hover:bg-indigo-50">
        <Send className="mr-1.5 h-3.5 w-3.5" /> {sending ? "Sending…" : "Send me a test"}
      </Button>
    </div>
  );
}

const PERIODS = [
  { v: "daily", label: "Last 14 days" },
  { v: "weekly", label: "Last 8 weeks" },
  { v: "monthly", label: "Last 12 months" },
];

export default function AiInsights() {
  const { user } = useAuth();
  const isCorp = user.role === "corporate_admin";
  const [period, setPeriod] = useState("monthly");
  const [locId, setLocId] = useState("org");
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    if (isCorp) api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, [isCorp]);

  const location_id = locId === "org" ? undefined : locId;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2.5">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#4338CA] text-white"><Sparkles className="h-5 w-5" /></span>
        <div><Label>Servantage AI</Label><h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">AI Assistant</h1></div>
      </div>

      <Tabs defaultValue="insights" className="space-y-5">
        <TabsList data-testid="ai-tabs">
          <TabsTrigger value="insights" data-testid="tab-ai-insights"><LineChart className="mr-1.5 h-4 w-4" /> Insights</TabsTrigger>
          <TabsTrigger value="ask" data-testid="tab-ai-ask"><MessageSquareText className="mr-1.5 h-4 w-4" /> Ask AI</TabsTrigger>
        </TabsList>

        <TabsContent value="insights" className="space-y-5">
          <DigestCard />
          <div className="flex flex-wrap items-center justify-end gap-2">
            {isCorp && (
              <Select value={locId} onValueChange={setLocId}>
                <SelectTrigger className="w-[200px]" data-testid="ai-location-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="org">All locations</SelectItem>
                  {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[170px]" data-testid="ai-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PERIODS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid gap-6 xl:grid-cols-2">
            <ExecutiveBrief period={period} locationId={location_id} />
            <CommentInsights locationId={location_id} />
          </div>
        </TabsContent>

        <TabsContent value="ask">
          <AiChat embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}
